import apiRequest from 'api/apiRequest';
import endpoints from 'api/endpoints';
import { HttpMethods } from 'api/types';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { RESULTS_PER_PAGE } from 'settings';
import { Store } from 'store';
import getLocaleSelector from 'store/app/selector/getLocaleSelector';
import { setFavouriteProductsAction } from 'store/favourite/slice';
import { setLandingPageData } from 'store/landing/slice';
import setPricesToFetchOperation from 'store/price/operation/setPricesToFetchOperation';
import dataFetchToken from 'utils/dataFetchToken';
import getFavProductsFromSearchResults from 'utils/getFavProductsFromSearchResults';
import globalVars from 'utils/globalVars';
import searchRedirectEvent from 'utils/gtm/gtmSearchRedirectEvent';
import gtmSendEventAsync from 'utils/gtm/gtmSendEventAsync';
import collectPricesFromSearchResults from 'utils/price/collectPricesFromSearchResults';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import gtmCuratedNoResultsEvent from 'utils/gtm/gtmCuratedNoResultsEvent';
import gtmSearchKeywordEvent from 'utils/gtm/gtmSearchKeyword';
import getOffset from '../selector/getOffset';
import getOffsetByPageNr from '../selector/getOffsetByPageNr';
import getSearchPageNr from '../selector/getSearchPageNr';
import getSearchQuery from '../selector/getSearchQuery';
import getSearchRefetchFlag from '../selector/getSearchRefetchFlag';
import getSearchSortBy from '../selector/getSearchSortBy';
import getTotalResultsAvailable from '../selector/getTotalResultsAvailable';
import getTotalResultsFetched from '../selector/getTotalResultsFetched';
import {
  registerFailedSearchRequestAction,
  resetFailedSearchRequestAction,
  resetSearchAction,
  setCurrentOffsetAction,
  setCurrentQueryAction,
  setSearchLoadingAction,
  setSearchRefetchFlagAction,
  setSearchResultsAction,
  setShowFallbackLandingPageAction,
  setTotalResultsAvailableAction,
  setTotalResultsFetchedAction,
} from '../slice';
import { SearchResponse } from '../types';
import { PublicAssetSources } from '../../publicAssetProduct/types';

const limit = RESULTS_PER_PAGE;

const isCollectionsPage = () => window.location.pathname.indexOf('/collections/') !== -1;

const getRequestParams = (store: Store, offset: number) => {
  const currentFilters = {
    ...store.search.settings.filters,
    ...store.search.settings.defaultFilters,
  };

  return {
    query: store.search.searchQuery,
    locale: getLocaleSelector(store),
    offset,
    limit: RESULTS_PER_PAGE,
    // BE want to have empty value for orderBy parameter for mostRelevant order
    orderBy: getSearchSortBy(store).replace('mostRelevant', ''),
    order: 'desc',
    filters: [
      ...Object.keys(currentFilters).map((id) => ({
        id,
        values: currentFilters[id],
      })),
      {
        id: 'productSource',
        values: [
          PublicAssetSources.Bridgeman,
          PublicAssetSources.Shutterstock,
          PublicAssetSources.ManualUpload,
          'optimalprint',
        ],
      },
    ],
  };
};

const fetchResultsForPage = async (
  pageNr: number,
  store: Store,
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
) => {
  const offset = getOffsetByPageNr(pageNr);
  const refetchFlag = getSearchRefetchFlag(store);

  const results = await apiRequest<SearchResponse>(
    endpoints.searchV2Regular,
    getRequestParams(store, Number(offset)),
    HttpMethods.POST,
    'data',
  );

  if (!results) {
    dispatch(registerFailedSearchRequestAction());
    return;
  }

  dispatch(resetFailedSearchRequestAction());
  if (!isCollectionsPage) {
    gtmSendEvent(gtmSearchKeywordEvent(results.query));
  }

  if (results.redirect) {
    if (typeof window === 'undefined') {
      return;
    }
    if (!isCollectionsPage()) {
      await gtmSendEventAsync(searchRedirectEvent(results.query));
      window.location.href = results.redirect;
    }
  }

  if (!results.products.length) {
    if (typeof window === 'undefined') {
      return;
    }
    if (!isCollectionsPage()) {
      gtmSendEvent(gtmCuratedNoResultsEvent(results.query));
    }
  }

  if (!results.products?.length && results.fallbackContent) {
    try {
      const fallbackContent = JSON.parse(results.fallbackContent);
      dispatch(setLandingPageData(fallbackContent));
    } catch (e) {
      console.error(e);
      dispatch(setLandingPageData(undefined as unknown as any));
    }
    dispatch(setShowFallbackLandingPageAction(true));
  }

  const prices = collectPricesFromSearchResults(results);
  dispatch(setPricesToFetchOperation(prices));

  if (refetchFlag) {
    dispatch(resetSearchAction());
    dispatch(setSearchRefetchFlagAction(false));
  }
  dispatch(setFavouriteProductsAction(getFavProductsFromSearchResults(results.products)));
  dispatch(setSearchResultsAction(results));
  dispatch(setTotalResultsAvailableAction(results.hits));
  dispatch(setTotalResultsFetchedAction(offset + (results.products?.length || 0)));
};

const pageIsNotFetched = (
  pageNr: number,
  refetchFlag: boolean,
  leftSegmentLimit: number,
  segmentSize: number,
  pageSize: number,
  totalFamilies: number,
) =>
  (pageNr === 1 && refetchFlag) ||
  (pageNr - 1) * pageSize < leftSegmentLimit ||
  Math.min(pageNr * pageSize, totalFamilies) > leftSegmentLimit + segmentSize;

const fetchSearchResultsOperation =
  (targetPage?: number) => async (dispatch: ThunkDispatch<Store, undefined, AnyAction>, getState: () => Store) => {
    try {
      globalVars.lastSearchRequestToken = dataFetchToken.next().value;
      let store = getState();
      const totalResultsAvailable = getTotalResultsAvailable(store) || 999999;
      const currentPageNr = targetPage || getSearchPageNr(store);

      dispatch(setCurrentQueryAction(getSearchQuery(store)));
      dispatch(setSearchLoadingAction(true));

      if (
        pageIsNotFetched(
          currentPageNr,
          getSearchRefetchFlag(store),
          getOffset(store),
          getTotalResultsFetched(store),
          limit,
          totalResultsAvailable,
        )
      ) {
        store = getState();
        await fetchResultsForPage(currentPageNr, store, dispatch);
      }
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(setSearchLoadingAction(false));
    }
  };

export default fetchSearchResultsOperation;
